import { audio } from '../../../audio/AudioManager.js';
import { SaveManager } from '../../../state/SaveManager.js';
import { applicationLifecycle } from '../../shared/lifecycle/index.js';
import {
  resolveStoryForestLayout,
  STORY_FOREST_LAYOUT_HEIGHT,
  STORY_FOREST_LAYOUT_WIDTH,
} from './components/StoryForestLayout.js';
import { STORY_FOREST_WORD_GARDEN } from './StoryForestConfig.js';
import {
  hasStoryForestProductionAssets,
  preloadStoryForestAssets,
  STORY_FOREST_ASSET_LIST,
  STORY_FOREST_ASSETS,
} from './StoryForestAssets.js';
import {
  completeWordGardenChallenge,
  getStoryForestState,
  markWordGardenDiscovered,
  STORY_FOREST_DISTRICTS,
  visitStoryForestDistrict,
} from './StoryForestState.js';
import {
  getMissingWordGardenAssets,
  hasWordGardenProductionAssets,
  preloadWordGardenAssets,
  WORD_GARDEN_ASSET_LIST,
  WORD_GARDEN_ASSET_REQUIREMENTS,
} from './minigames/word-garden/WordGardenAssets.js';
import { WordGardenChallenge } from './minigames/word-garden/WordGardenChallenge.js';
import type { WordGardenHint } from './minigames/word-garden/WordGardenConfig.js';
import type { WordGardenSnapshot } from './minigames/word-garden/WordGardenState.js';
import { canEnterCharacterGrove } from './minigames/character-grove/CharacterGroveAssets.js';
import { CHARACTER_GROVE_SCENE_KEY } from './CharacterGroveScene.js';

const FONT_READING = '"Nunito", Arial, sans-serif';

interface ChoiceView {
  readonly id: string;
  readonly root: Phaser.GameObjects.Container;
  readonly image: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
  readonly hitArea: Phaser.GameObjects.Zone;
}

interface TextButtonView {
  readonly root: Phaser.GameObjects.Container;
  readonly label: Phaser.GameObjects.Text;
  readonly hitArea: Phaser.GameObjects.Zone;
}

export class WordGardenScene extends Phaser.Scene {
  private renderScale = 1;
  private reducedMotion = false;
  private inputEnabled = false;
  private leaving = false;
  private gardenRestored = false;
  private resizeDebounce?: number;
  private unsubscribeLifecycle?: () => void;
  private challenge?: WordGardenChallenge;

  private background!: Phaser.GameObjects.Image;
  private foregroundLeft!: Phaser.GameObjects.Image;
  private foregroundRight!: Phaser.GameObjects.Image;
  private cosmo!: Phaser.GameObjects.Image;
  private scroll!: Phaser.GameObjects.Image;
  private plant!: Phaser.GameObjects.Image;
  private plantHitArea!: Phaser.GameObjects.Zone;
  private bloomVfx!: Phaser.GameObjects.Image;
  private titleText!: Phaser.GameObjects.Text;
  private targetWordText!: Phaser.GameObjects.Text;
  private sentenceText!: Phaser.GameObjects.Text;
  private clueText!: Phaser.GameObjects.Text;
  private feedbackText!: Phaser.GameObjects.Text;
  private instructionText!: Phaser.GameObjects.Text;
  private restoredWordText!: Phaser.GameObjects.Text;
  private homeButton!: TextButtonView;
  private storyTreeButton!: TextButtonView;
  private hintButton!: TextButtonView;
  private resetButton!: TextButtonView;
  private groveButton!: TextButtonView;
  private choiceViews: ChoiceView[] = [];

  constructor() {
    super({ key: 'WordGarden' });
  }

  preload() {
    preloadStoryForestAssets(this);
    preloadWordGardenAssets(this);
  }

  create() {
    this.choiceViews = [];
    this.challenge = undefined;
    this.inputEnabled = false;
    this.leaving = false;

    if (!hasStoryForestProductionAssets(this) || !hasWordGardenProductionAssets(this)) {
      const missing = getMissingWordGardenAssets(this);
      if (missing.length > 0) {
        console.error(
          '[WordGarden] Production textures failed to load:',
          missing.map(asset => `${asset.key} (${asset.path})`),
        );
      }
      visitStoryForestDistrict(STORY_FOREST_DISTRICTS.silentStoryTree);
      this.scene.start('StoryForest');
      return;
    }

    const state = getStoryForestState();
    if (state.storyTreeStage !== 'awakened-1'
        || !state.unlockedDistricts.includes(STORY_FOREST_DISTRICTS.wordGarden)) {
      visitStoryForestDistrict(STORY_FOREST_DISTRICTS.silentStoryTree);
      this.scene.start('StoryForest');
      return;
    }

    void audio.enterSoundscape('story-forest');
    this.renderScale = this.configureCrispRenderer();
    this.reducedMotion = SaveManager.getSettings().reducedMotion
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    [...STORY_FOREST_ASSET_LIST, ...WORD_GARDEN_ASSET_LIST].forEach(asset => {
      this.textures.get(asset.key).setFilter(Phaser.Textures.FilterMode.LINEAR);
    });

    const discovery = markWordGardenDiscovered();
    this.gardenRestored = discovery.state.completedWordGardenChallenges.includes(
      STORY_FOREST_WORD_GARDEN.challenge.id,
    );
    this.buildEnvironment();
    this.buildSafeUI();
    this.buildChallengePresentation();
    this.registerInput();
    this.registerLifecycleSafety();

    if (this.gardenRestored) {
      this.applyRestoredView();
    } else {
      this.challenge = new WordGardenChallenge(STORY_FOREST_WORD_GARDEN.challenge, {
        onStateChanged: snapshot => this.refreshChallenge(snapshot),
        onIncorrect: () => this.handleIncorrect(),
        onCompleted: config => this.handleSuccess(config.completionWorldEvent),
      });
      this.refreshChallenge(this.challenge.getSnapshot());
      this.feedbackText.setText(
        discovery.newlyDiscovered
          ? 'These magical plants have lost their meanings. Context can help them grow.'
          : 'Read the context, choose a meaning seed, then grow the plant.',
      );
    }

    this.layout();
    this.applyTextResolution(this.renderScale);
    this.cameras.main.fadeIn(this.reducedMotion ? 180 : 500, 231, 255, 224);
    this.inputEnabled = true;
  }

  private buildEnvironment() {
    const assets = WORD_GARDEN_ASSET_REQUIREMENTS;
    this.background = this.add.image(640, 360, assets.background.key)
      .setDepth(0)
      .setCrop(6, 6, 1440, 432)
      .setTint(0xc7d2c1);
    this.coverImage(this.background, 1440, 432);

    this.foregroundLeft = this.add.image(0, 720, assets.foliageLeft.key)
      .setOrigin(0, 1)
      .setCrop(7, 7, 397, 340)
      .setDisplaySize(292, 252)
      .setDepth(72)
      .setVisible(false);
    this.foregroundRight = this.add.image(1280, 720, assets.foliageRight.key)
      .setOrigin(1, 1)
      .setCrop(7, 7, 373, 340)
      .setDisplaySize(280, 252)
      .setDepth(72)
      .setVisible(false);
    this.cosmo = this.add.image(140, 615, STORY_FOREST_ASSETS.storyForestCosmo.key)
      .setDisplaySize(116, 154)
      .setDepth(80);
    this.bloomVfx = this.add.image(760, 510, assets.bloomVfx.key)
      .setCrop(18, 18, 918, 210)
      .setDisplaySize(900, 232)
      .setDepth(48)
      .setVisible(false)
      .setAlpha(0);
  }

  private buildSafeUI() {
    this.titleText = this.makeText('WORD GARDEN', 30, '#fff2b8', 'bold')
      .setStroke('#274c38', 7)
      .setLetterSpacing(2)
      .setDepth(101);
    this.homeButton = this.createTextButton('←  HOME HUB', () => this.returnHome(), 160);
    this.storyTreeButton = this.createTextButton('←  STORY TREE', () => this.returnStoryTree(), 180);
    this.groveButton = this.createTextButton('CHARACTER GROVE  →', () => this.enterCharacterGrove(), 205);
    this.groveButton.root.setVisible(false);
    this.instructionText = this.makeText('', 16, '#fff8dc', 'bold')
      .setStroke('#244632', 6)
      .setDepth(101);
  }

  private buildChallengePresentation() {
    const assets = WORD_GARDEN_ASSET_REQUIREMENTS;
    const config = STORY_FOREST_WORD_GARDEN.challenge;
    this.scroll = this.add.image(620, 222, assets.storyScroll.key).setDepth(18);
    this.targetWordText = this.makeText(config.targetWord.toLocaleUpperCase(), 25, '#7a431f', 'bold')
      .setDepth(21);
    this.sentenceText = this.makeText(config.sentence, 20, '#402b25', 'bold')
      .setWordWrapWidth(500)
      .setLineSpacing(5)
      .setDepth(21);
    this.clueText = this.makeText('', 17, '#785027', 'bold')
      .setWordWrapWidth(500)
      .setLineSpacing(4)
      .setDepth(21);
    this.feedbackText = this.makeText('', 16, '#fff9df', 'bold')
      .setStroke('#244632', 5)
      .setWordWrapWidth(760)
      .setLineSpacing(3)
      .setDepth(101);
    this.restoredWordText = this.makeText('', 22, '#fff3a8', 'bold')
      .setStroke('#244632', 6)
      .setDepth(61)
      .setVisible(false);

    this.plant = this.add.image(1040, 520, assets.plantDormant.key)
      .setDisplaySize(174, 230)
      .setDepth(55);
    this.plantHitArea = this.add.zone(1040, 520, 220, 270)
      .setDepth(56)
      .setInteractive({ useHandCursor: true });
    this.plantHitArea.on('pointerup', () => this.submitMeaning());

    this.choiceViews = config.choices.map(choice => {
      const root = this.add.container(0, 0).setDepth(54);
      const image = this.add.image(0, 0, assets.meaningSeed.key).setDisplaySize(92, 134);
      const label = this.makeText(choice.meaning, 16, '#fff9df', 'bold')
        .setStroke('#244632', 5)
        .setWordWrapWidth(170)
        .setPosition(0, 88);
      const hitArea = this.add.zone(0, 18, 170, 202).setInteractive({ useHandCursor: true });
      hitArea.on('pointerup', () => this.selectChoice(choice.id));
      root.add([image, label, hitArea]);
      return { id: choice.id, root, image, label, hitArea };
    });

    this.hintButton = this.createTextButton('?  CONTEXT HINT', () => this.showHint(), 190);
    this.resetButton = this.createTextButton('↺  NEW TRY', () => this.resetChallenge(), 160);
  }

  private createTextButton(label: string, action: () => void, width: number): TextButtonView {
    const root = this.add.container(0, 0).setDepth(102);
    const text = this.makeText(label, 16, '#fff8e7', 'bold').setStroke('#244632', 5);
    const hitArea = this.add.zone(0, 0, width, 48).setInteractive({ useHandCursor: true });
    hitArea.on('pointerdown', () => text.setScale(0.96).setAlpha(0.78));
    hitArea.on('pointerout', () => text.setScale(1).setAlpha(1));
    hitArea.on('pointerup', () => {
      text.setScale(1).setAlpha(1);
      action();
    });
    root.add([hitArea, text]);
    return { root, label: text, hitArea };
  }

  private makeText(
    value: string,
    size: number,
    color: string,
    fontStyle: 'normal' | 'bold',
  ) {
    return this.add.text(0, 0, value, {
      fontFamily: FONT_READING,
      fontSize: `${size}px`,
      fontStyle,
      color,
      align: 'center',
    }).setOrigin(0.5).setResolution(2);
  }

  private selectChoice(choiceId: string) {
    if (!this.inputEnabled || this.gardenRestored) return;
    if (this.challenge?.selectChoice(choiceId)) audio.click();
  }

  private submitMeaning() {
    if (!this.inputEnabled || this.gardenRestored || !this.challenge) return;
    const result = this.challenge.submit();
    if (result === 'incomplete') {
      this.feedbackText.setText('Choose a meaning seed first, then tap the plant to grow it.');
    }
  }

  private showHint() {
    if (!this.inputEnabled || this.gardenRestored || !this.challenge) return;
    const hint = this.challenge.requestHint();
    audio.hint();
    this.presentHint(hint);
  }

  private presentHint(hint: WordGardenHint) {
    this.feedbackText.setText(`Cosmo's hint: ${hint.message}`);
    this.clueText.setText(hint.highlightText ? `Helpful clue: “${hint.highlightText}”` : '');
  }

  private resetChallenge() {
    if (!this.inputEnabled || this.gardenRestored || !this.challenge) return;
    if (!this.challenge.reset()) return;
    audio.click();
    this.clueText.setText('');
    this.feedbackText.setText('Choose a meaning seed, then tap the plant to grow it.');
    this.plant.setTexture(WORD_GARDEN_ASSET_REQUIREMENTS.plantDormant.key)
      .setDisplaySize(174, 230)
      .setAngle(0);
  }

  private refreshChallenge(snapshot: WordGardenSnapshot) {
    const assets = WORD_GARDEN_ASSET_REQUIREMENTS;
    this.choiceViews.forEach(view => {
      const selected = view.id === snapshot.selectedChoiceId;
      view.image.setTexture(selected ? assets.meaningSeedSelected.key : assets.meaningSeed.key)
        .setDisplaySize(selected ? 101 : 92, selected ? 142 : 134);
      view.label.setColor(selected ? '#fff0a8' : '#fff9df');
    });
    if (snapshot.lastResult !== 'incorrect' && !snapshot.completed) {
      this.plant.setTexture(assets.plantDormant.key).setDisplaySize(174, 230).setAngle(0);
    }
    if (snapshot.selectedChoiceId && !snapshot.lastResult) {
      this.feedbackText.setText('That seed is ready. Tap the plant or press Enter to grow it.');
    }
  }

  private handleIncorrect() {
    if (!this.challenge) return;
    const hint = this.challenge.requestHint();
    audio.wrong();
    this.plant.setTexture(WORD_GARDEN_ASSET_REQUIREMENTS.plantWilt.key)
      .setDisplaySize(188, 214);
    this.presentHint(hint);
    if (!this.reducedMotion) {
      this.tweens.add({
        targets: this.plant,
        angle: 3,
        duration: 150,
        yoyo: true,
        repeat: 2,
        ease: 'Sine.easeInOut',
      });
    }
  }

  private handleSuccess(completionWorldEvent: string) {
    if (!this.challenge || this.gardenRestored) return;
    const result = completeWordGardenChallenge(
      STORY_FOREST_WORD_GARDEN.challenge.id,
      completionWorldEvent,
    );
    this.gardenRestored = true;
    this.inputEnabled = false;
    this.plant.setTexture(WORD_GARDEN_ASSET_REQUIREMENTS.plantBloom.key)
      .setDisplaySize(190, 224)
      .setAngle(0);
    this.plantHitArea.input!.enabled = false;
    this.choiceViews.forEach(view => { view.hitArea.input!.enabled = false; });
    this.hintButton.root.setVisible(false);
    this.resetButton.root.setVisible(false);
    this.background.clearTint();
    this.bloomVfx.setVisible(true).setAlpha(this.reducedMotion ? 0.72 : 0);
    this.restoredWordText
      .setText(`${STORY_FOREST_WORD_GARDEN.challenge.targetWord.toLocaleUpperCase()}  =  easy to damage`)
      .setVisible(true);
    this.feedbackText.setText(STORY_FOREST_WORD_GARDEN.challenge.successMessage);
    this.instructionText.setText('The Word Garden is restored! Explore, visit the Story Tree, or return Home.');
    this.groveButton.root.setVisible(canEnterCharacterGrove(this.textures));
    audio.restoration();
    if (!this.reducedMotion) {
      this.tweens.add({
        targets: this.bloomVfx,
        alpha: 0.9,
        duration: 620,
        ease: 'Sine.easeOut',
      });
      this.tweens.add({
        targets: this.plant,
        scaleX: this.plant.scaleX * 1.08,
        scaleY: this.plant.scaleY * 1.08,
        duration: 360,
        yoyo: true,
        repeat: 1,
        ease: 'Sine.easeInOut',
      });
      this.tweens.add({
        targets: this.cosmo,
        angle: -5,
        duration: 240,
        yoyo: true,
        repeat: 3,
        ease: 'Sine.easeInOut',
      });
    }
    this.time.delayedCall(this.reducedMotion ? 500 : 1500, () => {
      this.choiceViews.forEach(view => view.root.setVisible(false));
      this.inputEnabled = true;
      if (result.firstCompletion) audio.celebrate();
    });
  }

  private applyRestoredView() {
    this.background.clearTint();
    this.choiceViews.forEach(view => {
      view.root.setVisible(false);
      view.hitArea.input!.enabled = false;
    });
    this.hintButton.root.setVisible(false);
    this.resetButton.root.setVisible(false);
    this.plant.setTexture(WORD_GARDEN_ASSET_REQUIREMENTS.plantBloom.key)
      .setDisplaySize(190, 224);
    this.plantHitArea.input!.enabled = false;
    this.bloomVfx.setVisible(true).setAlpha(0.66);
    this.restoredWordText
      .setText(`${STORY_FOREST_WORD_GARDEN.challenge.targetWord.toLocaleUpperCase()}  =  easy to damage`)
      .setVisible(true);
    this.feedbackText.setText('The moonflower remembers its meaning, and the Word Garden is blooming.');
    this.instructionText.setText('Explore the restored garden, visit the Story Tree, or return Home.');
    this.groveButton.root.setVisible(canEnterCharacterGrove(this.textures));
  }

  private registerInput() {
    this.input.keyboard?.on('keydown', this.handleKeyDown, this);
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (!this.inputEnabled || this.leaving) return;
    if (event.key === 'Escape') {
      this.returnStoryTree();
      return;
    }
    if (event.key.toLocaleLowerCase() === 'g' && this.gardenRestored) {
      this.enterCharacterGrove();
      return;
    }
    if (this.gardenRestored || !this.challenge) return;
    if (event.key >= '1' && event.key <= '3') {
      const choice = STORY_FOREST_WORD_GARDEN.challenge.choices[Number(event.key) - 1];
      if (choice) this.selectChoice(choice.id);
      return;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      const snapshot = this.challenge.getSnapshot();
      const currentIndex = STORY_FOREST_WORD_GARDEN.challenge.choices.findIndex(
        choice => choice.id === snapshot.selectedChoiceId,
      );
      const direction = event.key === 'ArrowLeft' ? -1 : 1;
      const nextIndex = Phaser.Math.Wrap(
        currentIndex < 0 ? 0 : currentIndex + direction,
        0,
        STORY_FOREST_WORD_GARDEN.challenge.choices.length,
      );
      this.selectChoice(STORY_FOREST_WORD_GARDEN.challenge.choices[nextIndex].id);
      event.preventDefault();
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      this.submitMeaning();
      event.preventDefault();
      return;
    }
    if (event.key.toLocaleLowerCase() === 'h') {
      this.showHint();
      return;
    }
    if (event.key.toLocaleLowerCase() === 'r') this.resetChallenge();
  }

  private layout() {
    const { safe, compact } = resolveStoryForestLayout(this);
    const scrollWidth = Math.min(compact ? 570 : 620, safe.width - (compact ? 390 : 420));
    const scrollHeight = scrollWidth * (324 / 489);
    const scrollX = safe.centerX - (compact ? 40 : 55);
    const scrollY = safe.y + (compact ? 178 : 194);
    this.scroll.setPosition(scrollX, scrollY).setDisplaySize(scrollWidth, scrollHeight);
    this.titleText.setPosition(safe.centerX, safe.y + (compact ? 30 : 44))
      .setFontSize(compact ? 25 : 30);
    this.homeButton.root.setPosition(safe.x + 82, safe.y + (compact ? 28 : 38));
    this.storyTreeButton.root.setPosition(safe.right - 96, safe.y + (compact ? 28 : 38));
    this.groveButton.root.setPosition(safe.right - (compact ? 125 : 145), safe.bottom - (compact ? 28 : 38));
    this.targetWordText.setPosition(scrollX, scrollY - scrollHeight * 0.26)
      .setFontSize(compact ? 21 : 25);
    this.sentenceText.setPosition(scrollX, scrollY - scrollHeight * 0.03)
      .setFontSize(compact ? 18 : 20)
      .setWordWrapWidth(scrollWidth * 0.72);
    this.clueText.setPosition(scrollX, scrollY + scrollHeight * 0.26)
      .setFontSize(compact ? 15 : 17)
      .setWordWrapWidth(scrollWidth * 0.74);

    const seedY = safe.bottom - (compact ? 194 : 200);
    const seedGap = compact ? 198 : 220;
    const seedStart = safe.centerX - (compact ? 255 : 275);
    this.choiceViews.forEach((view, index) => {
      view.root.setPosition(seedStart + index * seedGap, seedY);
      view.label.setFontSize(compact ? 14 : 16).setWordWrapWidth(compact ? 145 : 165);
    });
    const plantX = safe.right - (compact ? 105 : 120);
    const plantY = safe.bottom - (compact ? 192 : 198);
    this.plant.setPosition(plantX, plantY);
    this.plantHitArea.setPosition(plantX, plantY);
    this.bloomVfx.setPosition(safe.centerX + 100, plantY + 5)
      .setDisplaySize(Math.min(900, safe.width - 120), compact ? 194 : 220);
    this.restoredWordText.setPosition(plantX - 15, plantY + (compact ? 126 : 132))
      .setFontSize(compact ? 16 : 18);
    this.cosmo.setPosition(safe.x + (compact ? 82 : 110), safe.bottom - 78);
    this.foregroundLeft.setPosition(safe.x - 8, safe.bottom);
    this.foregroundRight.setPosition(safe.right + 8, safe.bottom);
    this.feedbackText.setPosition(safe.centerX - 70, safe.bottom - (compact ? 68 : 70))
      .setFontSize(compact ? 14 : 16)
      .setWordWrapWidth(Math.min(720, safe.width - 360));
    this.instructionText.setPosition(safe.centerX, safe.bottom - 26)
      .setText(this.instructionText.text || '1–3 choose  •  Enter grow  •  H hint  •  R reset  •  Esc Story Tree')
      .setFontSize(compact ? 12 : 14);
    const controlY = scrollY + scrollHeight * 0.56;
    this.hintButton.root.setPosition(scrollX - 105, controlY);
    this.resetButton.root.setPosition(scrollX + 105, controlY);
  }

  private configureCrispRenderer() {
    const parentW = this.scale.parentSize.width || window.innerWidth;
    const parentH = this.scale.parentSize.height || window.innerHeight;
    const cssScale = Math.min(
      parentW / STORY_FOREST_LAYOUT_WIDTH,
      parentH / STORY_FOREST_LAYOUT_HEIGHT,
    );
    const dpr = Phaser.Math.Clamp(window.devicePixelRatio || 1, 1, 2);
    const renderScale = Phaser.Math.Clamp(cssScale * dpr, 1, 2);
    this.scale.scaleMode = Phaser.Scale.FIT;
    this.scale.autoCenter = Phaser.Scale.CENTER_BOTH;
    this.scale.displaySize.setAspectMode(Phaser.Scale.FIT);
    this.scale.setGameSize(
      Math.round(STORY_FOREST_LAYOUT_WIDTH * renderScale),
      Math.round(STORY_FOREST_LAYOUT_HEIGHT * renderScale),
    );
    this.scale.refresh();
    this.cameras.main.setZoom(renderScale).centerOn(640, 360);
    return renderScale;
  }

  private coverImage(image: Phaser.GameObjects.Image, width: number, height: number) {
    const scale = Math.max(STORY_FOREST_LAYOUT_WIDTH / width, STORY_FOREST_LAYOUT_HEIGHT / height);
    image.setDisplaySize(width * scale, height * scale);
  }

  private applyTextResolution(resolution: number) {
    this.children.list.forEach(gameObject => {
      if (gameObject instanceof Phaser.GameObjects.Text) gameObject.setResolution(resolution);
    });
  }

  private registerLifecycleSafety() {
    this.unsubscribeLifecycle = applicationLifecycle.subscribe({
      onSuspend: () => { this.inputEnabled = false; },
      onResume: () => { if (!this.leaving) this.inputEnabled = true; },
    });
    window.addEventListener('resize', this.handleWindowResize);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribeLifecycle?.();
      this.unsubscribeLifecycle = undefined;
      audio.leaveSoundscape({ id: 'story-forest' });
      window.removeEventListener('resize', this.handleWindowResize);
      if (this.resizeDebounce !== undefined) window.clearTimeout(this.resizeDebounce);
      this.input.keyboard?.off('keydown', this.handleKeyDown, this);
      this.tweens.killAll();
    });
  }

  private readonly handleWindowResize = () => {
    if (this.resizeDebounce !== undefined) window.clearTimeout(this.resizeDebounce);
    this.resizeDebounce = window.setTimeout(() => {
      if (!this.scene.isActive()) return;
      this.renderScale = this.configureCrispRenderer();
      this.layout();
      this.applyTextResolution(this.renderScale);
    }, 80);
  };

  private returnStoryTree() {
    if (this.leaving) return;
    this.leaving = true;
    this.inputEnabled = false;
    visitStoryForestDistrict(STORY_FOREST_DISTRICTS.silentStoryTree);
    this.cameras.main.fadeOut(this.reducedMotion ? 160 : 380, 231, 255, 224);
    this.time.delayedCall(this.reducedMotion ? 180 : 410, () => this.scene.start('StoryForest', {
      district: STORY_FOREST_DISTRICTS.silentStoryTree,
    }));
  }

  private enterCharacterGrove() {
    if (this.leaving || !this.gardenRestored || !canEnterCharacterGrove(this.textures)) return;
    this.leaving = true;
    this.inputEnabled = false;
    this.scene.start(CHARACTER_GROVE_SCENE_KEY);
  }

  private returnHome() {
    if (this.leaving) return;
    this.leaving = true;
    this.inputEnabled = false;
    SaveManager.setLastScene('StoryForest');
    this.cameras.main.fadeOut(this.reducedMotion ? 160 : 380, 231, 255, 224);
    this.time.delayedCall(this.reducedMotion ? 180 : 410, () => this.scene.start('HomeHub'));
  }
}
